import { z } from 'zod';
import { uuid, isoDateTime } from './common.js';
import { packageDto } from './packaging.js';
/** docs/design/01-operations-data-model.md section 7. */
export const fulfilmentStatus = z.enum(['planned', 'released', 'picking', 'picked', 'packing', 'packed', 'shipped', 'cancelled']);
export const shortReason = z.enum(['not_found', 'damaged', 'wrong_count', 'other']);
export const fulfilmentLineDto = z.object({
    id: uuid,
    orderLineId: uuid,
    variantId: uuid.nullable(),
    sku: z.string(),
    title: z.string(),
    variantTitle: z.string().nullable(),
    imageUrl: z.string().nullable(),
    quantity: z.number().int(),
    quantityPicked: z.number().int(),
    quantityShipped: z.number().int(),
    locationId: uuid.nullable(),
    locationCode: z.string().nullable(),
    shortReason: shortReason.nullable(),
    /** Where the variant can be picked in this warehouse, picking locations first, in walking order. */
    pickFrom: z.array(z.object({ locationId: uuid, locationCode: z.string(), locationBarcode: z.string(), locationType: z.string(), onHand: z.number().int() })),
    /** What a scan of the product may read: the SKU itself and any EAN/GTIN Navision knows for it. */
    barcodes: z.array(z.string()),
});
export const fulfilmentDto = z.object({
    id: uuid,
    number: z.string(),
    orderId: uuid,
    orderNumber: z.number().int(),
    orderExternalNumber: z.string().nullable(),
    shopName: z.string(),
    customerName: z.string().nullable(),
    warehouseId: uuid,
    warehouseCode: z.string(),
    batchId: uuid.nullable(),
    status: fulfilmentStatus,
    lineCount: z.number().int(),
    unitCount: z.number().int(),
    releasedAt: isoDateTime.nullable(),
    printedAt: isoDateTime.nullable(),
    pickedBy: z.string().nullable(),
    pickedAt: isoDateTime.nullable(),
    packedBy: z.string().nullable(),
    packedAt: isoDateTime.nullable(),
    shippedAt: isoDateTime.nullable(),
    trackingNumber: z.string().nullable(),
    pickNoteDocumentId: uuid.nullable(),
    deliveryNoteDocumentId: uuid.nullable(),
    createdAt: isoDateTime,
});
export const fulfilmentDetailDto = fulfilmentDto.extend({ lines: z.array(fulfilmentLineDto), packages: z.array(packageDto), contentWeightG: z.number().int() });
export const fulfilmentQuery = z.object({
    warehouseId: uuid.optional(),
    status: fulfilmentStatus.optional(),
    /** Shorthand for the floor screens: `pick` = released or picking, `pack` = picked or packing or packed. */
    stage: z.enum(['pick', 'pack']).optional(),
    orderId: uuid.optional(),
    q: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
});
/** Release an order: create a fulfilment with every line that is available now. */
export const releaseOrderRequest = z.object({
    /** Force a warehouse; otherwise the rule picks one. */
    warehouseId: uuid.optional(),
    /** Print the pick note on this printer right away. */
    printerId: uuid.optional(),
});
/** Release several orders to picking from the order list; each is released on its own. */
export const releaseOrdersRequest = z.object({ orderIds: z.array(uuid).min(1).max(200) });
export const releaseOrdersResult = z.object({
    results: z.array(z.object({ orderId: uuid, ok: z.boolean(), fulfilmentNumber: z.string().nullable(), linesBackordered: z.number().int(), error: z.string().nullable() })),
});
export const releaseOrderResult = z.object({
    fulfilmentId: uuid.nullable(),
    number: z.string().nullable(),
    warehouseCode: z.string().nullable(),
    linesAllocated: z.number().int(),
    linesBackordered: z.number().int(),
    backorder: z.array(z.object({ sku: z.string(), quantity: z.number().int(), available: z.number().int() })),
});
export const pickLineRequest = z.object({
    quantityPicked: z.number().int().min(0),
    locationId: uuid.nullable().optional(),
    shortReason: shortReason.nullable().optional(),
});
export const packRequest = z.object({
    note: z.string().trim().max(500).nullable().optional(),
});
export const shipFulfilmentRequest = z.object({
    trackingNumber: z.string().trim().max(100).nullable().optional(),
    printerId: uuid.optional(),
});
export const printDocumentRequest = z.object({
    printerId: uuid,
    copies: z.number().int().min(1).max(10).default(1),
});
//# sourceMappingURL=fulfilments.js.map